// ABOUTME: Runtime flag gating the in-app auth screens (login/registration/... served at /auth/*).
// ABOUTME: ON in every environment; OFF means the external auth.trainingendurance.com fallback.

// The in-app auth UI (Kratos flows rendered inside app.trainingendurance.*) is the
// default everywhere (VITE_INAPP_AUTH=1 in every .env.<mode>). The flag stays a
// RUNTIME flag (read at call time, not baked into the bundle) because the external
// auth.trainingendurance.com hub is still deployed as the fallback, reached three ways:
// legacy app wrappers (below), the served-config kill-switch, and redirectToLogin's
// loop breaker (model/navigation.ts) — auth is the highest-blast-radius surface, so
// flipping back must not need a rebuild+redeploy.
//
// Mobile uses the SAME in-app flow: email/password runs in the WebView on the
// first-party Kratos cookie, and OIDC buttons are gated separately per browser context
// (see nativeOidc.ts — Google blocks OAuth in embedded webviews, so the wrapper hands
// providers to a Custom Tab via the bridge).
//
// EXCEPTION — legacy app wrappers: APKs that predate the Custom Tab OIDC contract
// mark themselves only by the injected bridge, without the `__TE_AUTH_NATIVE__`
// document-start marker. They can't hand OIDC to a real browser, so they stay on
// the external auth-hub flow they shipped with (the combination that works there,
// Google included). This wins over every override below — deterministic for support.
//
// Sources, in priority order (first defined wins):
//   0. Legacy wrapper (bridge present, no __TE_AUTH_NATIVE__) — HARD OFF, see above.
//   1. ?inapp_auth=1|0 — QA / dogfood override. Persisted to sessionStorage so it
//      survives in-app navigations within the session (e.g. / → /auth/login), then
//   2. the sticky sessionStorage value keeps it on for the rest of the session.
//   3. window.__INAPP_AUTH__ — served config: the deploy/serving layer injects this
//      boolean global into index.html. An explicit true/false here is the instant
//      runtime kill-switch and overrides the build-time default below.
//   4. VITE_INAPP_AUTH (build-time, per-env default in .env.<mode>) — ON in every
//      environment: Kratos is served under the app host everywhere.
// Absent all → false, the external redirect.

import config from "@/config"

const QUERY_PARAM = "inapp_auth"
const STORAGE_KEY = "inapp_auth"

// Build-time per-environment default (VITE_INAPP_AUTH in the active .env.<mode>).
const envDefault = (): boolean => parseBool(config.inAppAuthDefault())

declare global {
  interface Window {
    __INAPP_AUTH__?: boolean
    /** Injected by the native app at document start (new APKs only) — marks a
     *  wrapper that supports the native auth contract (Custom Tab OIDC). */
    __TE_AUTH_NATIVE__?: boolean
  }
}

// Old APK wrapper: the bridge is injected but the native-auth marker is not.
// (Inlined webkit check — mirrors NativeBridge.isAvailable() without pulling
// the singleton's import side-effects into this hot early-path flag.)
const isLegacyWrapper = (): boolean =>
  typeof window !== "undefined" &&
  window.webkit?.messageHandlers.nativeBridge !== undefined &&
  window.__TE_AUTH_NATIVE__ !== true

const parseBool = (raw: string): boolean => raw === "1" || raw === "true"

const readQueryOverride = (): boolean | undefined => {
  if (typeof window === "undefined") return undefined
  const raw = new URLSearchParams(window.location.search).get(QUERY_PARAM)
  if (raw === null) return undefined
  return parseBool(raw)
}

export const isInAppAuthEnabled = (): boolean => {
  if (typeof window === "undefined") return false

  // Legacy app wrappers stay on the external hub flow — wins over every
  // override below (see the note at the top of this file).
  if (isLegacyWrapper()) return false

  const query = readQueryOverride()
  if (query !== undefined) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, query ? "1" : "0")
    } catch {
      /* sessionStorage unavailable — fall back to per-URL behavior */
    }
    return query
  }

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    if (stored !== null) return parseBool(stored)
  } catch {
    /* ignore */
  }

  // Served-config global wins over the build-time default (instant runtime kill-switch):
  // an explicit true/false here overrides VITE_INAPP_AUTH either way.
  if (typeof window.__INAPP_AUTH__ === "boolean") return window.__INAPP_AUTH__

  return envDefault()
}

// Turn in-app auth OFF for the rest of the session (sticky). Used as a self-healing
// fallback when the in-app flow loops (see redirectToLogin) — reverts to the external flow.
export const disableInAppAuth = (): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, "0")
  } catch {
    /* ignore */
  }
}
